import { Group, Modal, SegmentedControl, Select, Stack, Table, Text } from '@mantine/core'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
	CourseAiLearnerQuery,
	CourseAiRevenueByDay,
	CourseAiRevenueFigures,
	CourseAiRevenueMonthRow,
	getCourseAiLearnersRevenueByDay,
	getCourseAiLearnersRevenueByMonth,
	unwrapObject,
} from 'src/apis/courseAiAPI'
import {
	formatVnd,
	revenueHasRefunds,
	revenueNet,
	revenueRefunds,
	sumRevenue,
} from './learnerDisplay'

type View = 'month' | 'day'

const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ||
	error?.response?.data?.results?.message ||
	fallback

const REFUND_COLOR = '#c92a2a'

function RevenueTable({
	label,
	rows,
	totals,
}: {
	label: string
	rows: (CourseAiRevenueFigures & { key: string })[]
	totals: CourseAiRevenueFigures
}) {
	// Gross, refunds and net are shown apart only where there is a refund to show.
	const withRefunds = revenueHasRefunds([...rows, totals])
	const cells = (row: CourseAiRevenueFigures) => (
		<>
			<td>{formatVnd(row.revenue)}</td>
			<td>{row.transactions}</td>
			<td>
				{row.resume_transactions} ({formatVnd(row.resume_revenue)})
			</td>
			{withRefunds ? (
				<>
					<td style={{ color: revenueRefunds(row) ? REFUND_COLOR : undefined }}>
						{revenueRefunds(row)
							? `-${formatVnd(revenueRefunds(row))} (${row.refund_count || 0})`
							: '-'}
					</td>
					<td>{formatVnd(revenueNet(row))}</td>
				</>
			) : null}
		</>
	)

	return (
		<Table striped fontSize="sm">
			<thead>
				<tr>
					<th>{label}</th>
					<th>{withRefunds ? 'Gross revenue' : 'Revenue'}</th>
					<th>Payments</th>
					<th>Of which resume fees</th>
					{withRefunds ? (
						<>
							<th>Refunds</th>
							<th>Net revenue</th>
						</>
					) : null}
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.key}>
						<td>{row.key}</td>
						{cells(row)}
					</tr>
				))}
			</tbody>
			<tfoot>
				<tr style={{ fontWeight: 700 }}>
					<td>Total</td>
					{cells(totals)}
				</tr>
			</tfoot>
		</Table>
	)
}

/**
 * The revenue table behind the Total Revenue card: by month, or the days of
 * one month. Both read the same payments under the same filters as the
 * learners table, so the days of a month add up to that month.
 */
function LearnerRevenueModal({
	opened,
	onClose,
	filters,
}: {
	opened: boolean
	onClose: () => void
	filters: CourseAiLearnerQuery
}) {
	const [view, setView] = useState<View>('month')
	const [months, setMonths] = useState<CourseAiRevenueMonthRow[]>([])
	const [monthsLoading, setMonthsLoading] = useState(false)
	const [month, setMonth] = useState<string | null>(null)
	const [days, setDays] = useState<CourseAiRevenueByDay | null>(null)
	const [daysLoading, setDaysLoading] = useState(false)

	useEffect(() => {
		if (!opened) return undefined
		let active = true
		setMonthsLoading(true)
		getCourseAiLearnersRevenueByMonth(filters)
			.then((response) => {
				if (!active) return
				const rows = unwrapObject<{ rows: CourseAiRevenueMonthRow[] }>(response)?.rows || []
				setMonths(rows)
				// The newest month is the first row.
				setMonth((current) =>
					current && rows.some((row) => row.month === current) ? current : rows[0]?.month || null,
				)
			})
			.catch((error) => {
				if (!active) return
				toast.error(errorMessage(error, 'Could not load revenue by month'))
				setMonths([])
			})
			.finally(() => {
				if (active) setMonthsLoading(false)
			})
		return () => {
			active = false
		}
	}, [opened, filters])

	useEffect(() => {
		if (!opened || view !== 'day' || !month) {
			setDays(null)
			return undefined
		}
		let active = true
		setDaysLoading(true)
		getCourseAiLearnersRevenueByDay({ ...filters, month })
			.then((response) => {
				if (active) setDays(unwrapObject<CourseAiRevenueByDay>(response))
			})
			.catch((error) => {
				if (!active) return
				toast.error(errorMessage(error, 'Could not load revenue by day'))
				setDays(null)
			})
			.finally(() => {
				if (active) setDaysLoading(false)
			})
		return () => {
			active = false
		}
	}, [opened, view, month, filters])

	const loading = monthsLoading || (view === 'day' && daysLoading)

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={view === 'day' ? 'Revenue by day (Vietnam time)' : 'Revenue by month (Vietnam time)'}
			size="xl"
		>
			<Stack spacing="sm">
				<Group position="apart" align="flex-end">
					<SegmentedControl
						value={view}
						onChange={(value) => setView(value as View)}
						data={[
							{ value: 'month', label: 'By month' },
							{ value: 'day', label: 'By day' },
						]}
					/>
					{view === 'day' ? (
						<Select
							label="Month"
							placeholder="Choose a month"
							data={months.map((row) => ({ value: row.month, label: row.month }))}
							value={month}
							onChange={setMonth}
							withinPortal
							sx={{ width: 150 }}
						/>
					) : null}
				</Group>

				{loading ? (
					<Text size="sm">Loading...</Text>
				) : months.length === 0 ? (
					<Text size="sm" color="dimmed">
						No payments in this range.
					</Text>
				) : view === 'month' ? (
					<RevenueTable
						label="Month"
						rows={months.map((row) => ({ ...row, key: row.month }))}
						totals={sumRevenue(months)}
					/>
				) : days ? (
					<RevenueTable
						label="Day"
						rows={days.rows.map((row) => ({ ...row, key: row.day }))}
						totals={days.totals}
					/>
				) : (
					<Text size="sm" color="dimmed">
						Choose a month.
					</Text>
				)}

				<Text size="xs" color="dimmed" align="right">
					Successful OnePay payments only, same filters as the table. A refund is taken off on the
					day staff recorded it.
				</Text>
			</Stack>
		</Modal>
	)
}

export default LearnerRevenueModal
